/* eslint-disable complexity */
import { ChangeEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { capitalize, debounce } from "lodash";
import { useNavigate } from "react-router";

import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  InputBase,
  Paper,
  Popper,
  Stack,
} from "@mui/material";
import { css, Theme } from "@mui/system";
import { Text } from "@imminently/react/ui/core/text";

import { Icon } from "@/components/Icon";
import { PersonIcon } from "@/assets/icons";
import { ExternalRegisterDot } from "@/components/externalregister/ExternalRegisterDot";
import { getPatientExternalRegisterAlerts, type PatientWithExternalRegisterSummary } from "@/components/externalregister/patientSummary";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import { patientService } from "@/services";
import { calculateAgeMoment } from "@/global";
import { getLoadEncounterRoute } from "@/pages/load-encounter";
import { getSearchRoute } from "@/pages/search";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { canRenderExternalRegisterDotIndicator } from "@/util/externalRegister";

const styles = {
  search: (theme: Theme) => css`
    // need to extra space
    margin-left: 1rem;
    background-color: white;

    &:focus-within {
      background-color: white;
    }

    svg {
      color: ${theme.palette.primary[500]};
    }
  `,
};

const WIDTH = 525; // Width of the Search Bar
const sxStyles = {
  search: {
    px: 1.5,
    py: 0,
    borderRadius: 1,
    width: WIDTH,
    alignItems: "center",
  },
  popper: {
    p: 2,
    mt: 0.5,
    width: WIDTH,
    borderRadius: 1,
    bgcolor: "white",
    zIndex: 1201,
  },
};
const DOT = "•";

export const PatientCard = ({
  patient,
  onClick,
  className,
  showExternalRegisterDot = true,
}: {
  patient: PatientWithExternalRegisterSummary;
  onClick: (id: string) => void;
  className?: string;
  showExternalRegisterDot?: boolean;
}) => {
  const { firstName, lastName, sex, dateOfBirth, umrn, email, phone } =
    patient;

  const fullName = [lastName.toUpperCase(), capitalize(firstName)].join(", ");
  const age = dateOfBirth ? calculateAgeMoment(dateOfBirth) : "-";
  const sexAge = `${sex ? sex[0].toUpperCase() : "-"} (${age})`;

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClick(patient.id);
  };

  return (
    <Card elevation={0} sx={{ backgroundColor: "#FAFBFF" }} className={className}>
      <CardActionArea onClick={handleClick} sx={{ height: "100%" }}>
        <CardContent sx={{ height: "100%" }}>
          <Stack direction="row" gap={0.75} alignItems="center">
            <Text variant="h5">{fullName}</Text>
            {showExternalRegisterDot ? (
              <ExternalRegisterDot alerts={getPatientExternalRegisterAlerts(patient)} />
            ) : null}
          </Stack>
          {(phone || email) && (
            <Stack direction="row" gap={1}>
              {email && <Text variant="body2">{email}</Text>}
              {phone && (
                <>
                  {email && <Text variant="body2">{DOT}</Text>}
                  <Text variant="body2">{phone}</Text>
                </>
              )}
            </Stack>
          )}
          <Stack direction="row" gap={1}>
            <Text variant="body2">{sexAge}</Text>
            {umrn && (
              <>
                <Text variant="body2">{DOT}</Text>
                <Text variant="body2">{umrn}</Text>
              </>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

type HandleChange = ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;

const PatientSearch = () => {
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);
  const { isConciergeMode } = useConciergeMode();
  const { settings } = useSettingsContext();
  const showExternalRegisterDot = canRenderExternalRegisterDotIndicator({
    isConciergeMode,
    settings,
  });

  const { data, isFetched, isLoading, isFetching, refetch, remove } =
    patientService.useGetList({
      enabled: false,
      meta: {
        search: `*${search}*`,
        // sort by last modified as they are more likely relevant
        sort: {
          field: "lastModified",
          order: "desc",
        },
      },
    }) as any;

  const patients = search.length > 0 ? (data?.data ?? []) : [];
  const total = search.length > 0 ? (data?.total ?? 0) : 0;

  const debouncedSearch = useCallback(
    debounce((val) => {
      if (val.length > 0) {
        refetch();
      }
    }, 500),
    []
  );

  const changeSearch: HandleChange = (event) => {
    const val = event.currentTarget.value;
    setSearch(val);
    if (val.length > 0) {
      setShowResults(true);
      debouncedSearch(val);
    } else {
      setShowResults(false);
    }
  };

  const onSelectPatient = (id: string) => {
    const route = getLoadEncounterRoute(id);
    navigate(route);
    setSearch("");
    // hide results but keep search term
    setShowResults(false);
  };

  const clearSearch = () => {
    setSearch("");
    setShowResults(false);
    // Clear cached query results
    if (remove) {
      remove();
    }
  };

  const searchRoute = getSearchRoute(search);
  const navigateToFullSearch = () => {
    setShowResults(false);
    navigate(searchRoute);
  };
  const showSearchResults = search.length > 0 && isFetched && !isLoading && showResults;

  // Handle click outside to hide search results (but keep search term)
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showSearchResults]);

  // Show results when user focuses on input with existing search
  const handleInputFocus = () => {
    if (search.length > 0 && isFetched) {
      setShowResults(true);
    }
  };

  return (
    <Stack
      ref={searchRef}
      css={styles.search}
      sx={sxStyles.search}
      direction="row"
    >
      <Icon icon="search" />
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder="Search patients"
        value={search}
        onChange={changeSearch}
        onFocus={handleInputFocus}
      />
      {search && (
        <IconButton
          size="small"
          onClick={clearSearch}
          sx={{ p: 0.5 }}
        >
          <Icon icon="close" />
        </IconButton>
      )}
      <Popper
        open={showSearchResults}
        anchorEl={searchRef.current}
        sx={{ zIndex: 1201 }}
      >
        <Paper sx={sxStyles.popper}>
          <Stack gap={1}>
            {!isFetching && total > 0 ? (
              <>
                <Stack direction="row" alignItems="center" gap={1}>
                  <PersonIcon />
                  <Text
                    variant="overline"
                    bold
                    sx={{ textTransform: "uppercase" }}
                  >
                    patients ({total})
                  </Text>
                </Stack>

                <Stack gap={1}>
                  {patients.slice(0, 4).map((p: any) => (
                    <PatientCard
                      key={p.id}
                      patient={p}
                      onClick={onSelectPatient}
                      showExternalRegisterDot={showExternalRegisterDot}
                    />
                  ))}
                </Stack>

                <Stack direction="row" justifyContent="end">
                  <Button
                    variant="text"
                    startIcon={<Icon icon="arrowright" />}
                    onClick={navigateToFullSearch}
                  >
                    See all results
                  </Button>
                </Stack>
              </>
            ) : (
              <Text variant="body2" sx={{ textAlign: "center" }}>
                {isFetching ? "Loading..." : "No results"}
              </Text>
            )}
          </Stack>
        </Paper>
      </Popper>
    </Stack>
  );
};

export default PatientSearch;
